<template>
  <div class="scene-text-menu">
    <item-header
      :label="$t('objectNames.text')"
      @close="$emit('close')"
    >
      <template #content>
        <editable-item-title :item="ctx.sceneNode" />
      </template>
    </item-header>

    <menu-group>
      <input-text-editor
        class="mv-2"
        :value="ctx.textValue"
        :placeholder="$t('labels.value')"
        :formattable="ctx.data?.stylable"
        @update:value="ctx.textValue = $event"
      />
    </menu-group>

    <drawer
      :title="$t('labels.design')"
      :open="drawerStates.textDesign"
      @update:open="drawerStates.setOpen('textDesign', $event)"
    >
      <div class="column">
        <div class="line">
          <input-color-text
            :value="ctx.sceneNode.textColor || null"
            popout-class="p-3"
            @update:value="ctx.updateKey('textColor', $event)"
          />
          <div class="input-sidelabel" :title="$t('labels.fontSize')">
            <label for="input-scene-text-font-size">
              <lucide-icon name="a-large-small" />
            </label>
            <input-number id="input-scene-text-font-size" :value="fontSize" @update:value="fontSize = $event" />
          </div>
          <div class="input-sidelabel" :title="$t('labels.lineHeight')">
            <label for="input-scene-text-line-height" :title="$t('labels.lineHeight')">
              <lucide-icon name="list-chevrons-down-up" />
            </label>
            <input-number id="input-scene-text-line-height" :value="ctx.sceneNode.lineHeight ?? 1.15" :step="0.05" @update:value="ctx.updateKey('lineHeight', $event)" />
          </div>
        </div>

        <div class="column gap-1 mt-4">
          <input-switch
            :value="ctx.sceneNode.visible"
            @update:value="ctx.updateKey('visible', $event)"
          >
            {{ $t("labels.visible") }}
          </input-switch>
        </div>
      </div>
    </drawer>

    <!-- <drawer
      :title="$t('labels.position')"
    >
      <div class="line pt-1">
        <div class="input-sidelabel" title="x">
          <label for="input-scene-text-x">x</label>
          <input-number id="input-scene-text-x" :value="ctx.sceneNode.x" :min="-Infinity" @update:value="ctx.updateKey('x', $event)" />
        </div>
        <div class="input-sidelabel" title="y">
          <label for="input-scene-text-y">y</label>
          <input-number id="input-scene-text-y" :value="ctx.sceneNode.y" :min="-Infinity" @update:value="ctx.updateKey('y', $event)" />
        </div>
      </div>
    </drawer> -->

    <drawer
      :title="$t('labels.advanced')"
      :open="drawerStates.textAdvanced"
      @update:open="drawerStates.setOpen('textAdvanced', $event)"
    >
      <div class="column">
        <input-switch :value="ctx.data?.connected" @update:value="ctx.updateData({ connected: $event })" >
          {{ $t("labels.connectedToProperty") }}
        </input-switch>

        <template v-if="ctx.data?.connected">
          <input-dropdown
            :options="connectableItems"
            :value="connectedItem"
            :searchable="true"
            @update:value="ctx.updateData({ connectedUid: $event?.uid })"
          >
            <template #default="{ item }">
              <div class="line-item">
                <store-item-icon :type="item.type" />
                <span>{{ item.title }}</span>
              </div>
            </template>
          </input-dropdown>

          <input-dropdown
            v-if="connectedItem"
            :options="connectablePropertyOption"
            :value="connectedPropertyOption"
            :searchable="true"
            v-slot="{ item }"
            @update:value="setConnectedProperty($event)"
          >
            {{ item.label }}
          </input-dropdown>
        </template>
      </div>
    </drawer>

    <menu-group class="mt-auto">
      <button
        class="button button_secondary mv-2"
        type="button"
        @click="deleteText"
      >
        {{ $t("actions.deleteText", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import Drawer from "@core/ui/DrawerBlock.vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
// import ButtonItem from "@core/input/ButtonItem.vue";

import InputTextEditor from "@core/input/TextEditor.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputColorText from "@core/input/InputColorText.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import StoreItemIcon from "../StoreItemIcon.vue";

import ItemHeader from "@editor/components/ItemHeader.vue";

import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useFeatures } from "@editor/store/features.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useStakeholderStore } from "../stakeholder/stakeholders.store";
import { splitUid, type Uid } from "@/helpers/utility/Id";
import { useSceneText } from "./useSceneText";
import { useSceneTree } from "../scene/sceneTree";

interface Props {
  nodeUid: Uid<"sceneNode">;
}
const props = defineProps<Props>();

defineEmits<{
  (event: "close"): void;
}>();

const { t } = useI18n();

const drawerStates = useDrawerStates();
const features = useFeatures();

const sceneTree = useSceneTree();
const ctx = useSceneText(() => splitUid(props.nodeUid).id);

// ======================
// CONNECT

const connectableItems = computed(() => {
  const items = [];
  if (features.target) items.push(...useTargetStore().orderedItems);
  if (features.resource) items.push(...useResourceStore().orderedItems);
  if (features.index) items.push(...useIndexStore().orderedItems);
  if (features.stakeholder) items.push(...useStakeholderStore().orderedItems);
  return items;
});

const connectedItem = computed(() => {
  return connectableItems.value.find(item => item.uid === ctx.data?.connectedUid);
});

const connectablePropertyOption = computed(() => {
  if (!connectedItem.value) return [];
  let props: string[] = [];
  switch (connectedItem.value.type) {
    case "target":
      props = ["title", "description"];
      break;
    case "resource":
      props = ["title", "description"];
      break;
    case "index":
      props = ["title", "description"];
      break;
    case "stakeholder":
      props = ["title", "description"];
      break;
    default:
      props = [];
  }
  return props.map((p) => ({ id: p, label: t(`labels.${p}`), stylable: p === "description" }));
});
const connectedPropertyOption = computed(() => {
  return connectablePropertyOption.value.find(p => p.id === ctx.data?.connectedProperty) || null;
});

function setConnectedProperty(property: { id: string; stylable: boolean } | null) {
  ctx.updateData({
    connectedProperty: property?.id || null,
    stylable: property?.stylable ?? true
  });
}

// ======================
// FONT SIZE

const sceneFontSize = computed(() => sceneTree.activeScene.fontSize);

const fontSize = computed({
  get() {
    if (ctx.sceneNode?.fontSizeRelative != null) {
      return sceneFontSize.value * ctx.sceneNode.fontSizeRelative;
    }
    return sceneFontSize.value;
  },
  set(value: number) {
    if (!ctx.sceneNode) return;
    const relativeSize = value / sceneFontSize.value;
    ctx.updateKey("fontSizeRelative", relativeSize);
  }
});

// ======================
// DELETE

function deleteText() {
  return ctx.delete();
}
</script>

<style lang="scss">
.scene-text-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
